import { toastManager } from '@insurancemenu/imenu-components-lib/dist/components/Toast/toastManager';
import { ToastColorTypes } from '@insurancemenu/imenu-components-lib/dist/types';
import { getSelfRegistrationLink, shareSelfRegistrationLink } from 'api/operations/userService';
import { action, computed, makeObservable, observable, runInAction } from 'mobx';
import { createContext, useContext } from 'react';
import { SendOutEmailsModalStore } from 'store/general/sendOutEmailsModalStore';

interface DomainParams {
  groupId: string;
}

export class EmployeeSelfRegisterLinkPageStore {
  private _groupId;
  private _isLoading = false;
  private _selfRegisterLink: string = '';

  // UI stores
  public sendOutEmailsModalStore = new SendOutEmailsModalStore({ onSubmit: this.sendEmailsWithLink.bind(this) });

  get isLoading(): boolean {
    return this._isLoading;
  }

  get selfRegisterLink(): string {
    return this._selfRegisterLink;
  }

  public setLoading(status: boolean): void {
    this._isLoading = status;
  }

  public async getInitialData(): Promise<void> {
    this.setLoading(true);
    const link = await getSelfRegistrationLink(this._groupId);
    runInAction(() => {
      this._selfRegisterLink = link;
      this.setLoading(false);
    });
  }

  public async sendEmailsWithLink(): Promise<void> {
    this.setLoading(true);

    const { message, ...recipients } = this.sendOutEmailsModalStore.form.getFormDataObject();
    await shareSelfRegistrationLink(this._groupId, Object.values(recipients), message);
    runInAction(() => {
      this.setLoading(false);
      toastManager.addToast('The registration link was successfully sent.', ToastColorTypes.SUCCESS);
    });
  }

  constructor(params: DomainParams) {
    this._groupId = params.groupId;
    makeObservable<EmployeeSelfRegisterLinkPageStore, '_isLoading' | '_groupId' | '_selfRegisterLink'>(this, {
      _groupId: observable,
      _isLoading: observable,
      _selfRegisterLink: observable,
      isLoading: computed,
      selfRegisterLink: computed,
      getInitialData: action.bound,
      sendEmailsWithLink: action.bound,
      setLoading: action.bound,
    });
  }
}

const EmployeeSelfRegisterLinkPageStoreContext = createContext(
  new EmployeeSelfRegisterLinkPageStore({ groupId: 'stub-group-id' }),
);

const useEmployeeSelfRegisterLinkPageStore = (): EmployeeSelfRegisterLinkPageStore => {
  return useContext(EmployeeSelfRegisterLinkPageStoreContext);
};

export { EmployeeSelfRegisterLinkPageStoreContext, useEmployeeSelfRegisterLinkPageStore };
